import { FileSpreadsheet, FileUp, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/ui/button/index.js";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";

const fileActionFocus =
  "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-ring)]";

const fileActionButton =
  `h-8 gap-1 rounded-md border-0 bg-transparent px-2 text-[0.75rem] font-medium shadow-none hover:bg-surface-muted ${fileActionFocus}`;

export function ImportSelectedFileBar({
  fileName,
  rowCount,
  disabled,
  onChangeFile,
  onRemoveFile,
}: {
  fileName: string;
  rowCount: number;
  disabled: boolean;
  onChangeFile: () => void;
  onRemoveFile: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const rows = rowCount.toLocaleString(i18n.language);

  return (
    <div
      className="flex min-h-10 items-center gap-2 border-b border-line py-2 text-start"
      data-testid="contact-import-selected-file"
    >
      <FileSpreadsheet size={15} className="shrink-0 text-muted" aria-hidden="true" />
      <p className="m-0 flex min-w-0 flex-1 items-baseline gap-2 text-[0.8125rem]">
        <bdi className="min-w-0 truncate font-medium [unicode-bidi:isolate]" dir="auto" title={fileName}>
          {fileName}
        </bdi>
        <span className="shrink-0 tabular-nums text-[0.75rem] text-muted">{rows}</span>
      </p>
      <div className="flex shrink-0 items-center gap-0.5 border-s border-line/80 ps-1.5">
        <SimpleTooltip content={t("contacts.import.changeFile")}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onChangeFile}
            className={`${fileActionButton} text-foreground`}
            aria-label={t("contacts.import.changeFile")}
            data-testid="contact-import-change-file"
          >
            <FileUp size={14} aria-hidden="true" />
            <span>{t("contacts.import.changeFile")}</span>
          </Button>
        </SimpleTooltip>
        <SimpleTooltip content={t("contacts.import.removeFile")}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onRemoveFile}
            className={`${fileActionButton} text-muted not-disabled:hover:text-destructive focus-visible:text-destructive`}
            aria-label={t("contacts.import.removeFile")}
            data-testid="contact-import-remove-file"
          >
            <Trash2 size={14} aria-hidden="true" />
            <span>{t("contacts.import.removeFile")}</span>
          </Button>
        </SimpleTooltip>
      </div>
    </div>
  );
}
